import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { captureException } from '@sentry/react';
import { Plans, PriceTable } from 'components/account/StoreDetails/PriceTable';
import querystring from 'querystring';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import { useAppSelector } from 'store';
import { useSelectSubscriptionPlanMutation } from 'store/services/segmentsApi';
import { SubscriptionPlan } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';
import { usePageTitle } from 'utils';
import { useQuery } from 'utils/useQuery';

export default function ChangePlanPage() {
  usePageTitle('Change plan | Segments');

  const query = useQuery();
  const history = useHistory();
  const plan = query.get('plan');

  const storeName = query.get('store_name');

  const { currentStore } = useAppSelector(userDetailsState);
  const [selectPlan, { isLoading }] = useSelectSubscriptionPlanMutation();

  const handleSelectPlan = (planName: SubscriptionPlan) => {
    if (!!!storeName || isLoading) return;
    selectPlan({
      serializedStoreName: querystring.stringify({ store_name: storeName }),
      plan: planName,
    })
      .unwrap()
      .then((response) => {
        toast.success(
          `You’ve selected the ${plan} plan. You will be redirected to Shopify to accept the plan`
        );

        window.location.href = response.confirmation_url;
      })
      .catch((error) => {
        captureException(error);
      });
  };

  useEffect(() => {
    if (storeName && plan) {
      handleSelectPlan(plan.toLowerCase() as SubscriptionPlan);
    }

    if (!storeName && currentStore.completeInfo?.store_name) {
      query.set('store_name', currentStore.completeInfo?.store_name);
      history.replace({
        search: query.toString(),
      });
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plans: Plans = [
    {
      id: 3,
      label: 'Free',
      name: 'free',
      price: 0,
      description: ['Up to $200k USD annual revenue', 'Up to 10k customers'],
      features: [
        'Pre-built customer segments',
        '30 days of data analysis',
        '50 customers per export',
        '1 team account',
        'Basic analytics',
      ],
      featured: false,
    },
    {
      id: 2,
      label: 'Small business',
      name: 'small',
      price: 79,
      description: [
        'Best for new businesses building out your niche',
        'Pre $1m USD annual revenue',
        'Under 50k customers',
      ],
      features: [
        '1 year of data analysis',
        '5,000 customers per sync',
        '2 team account',
        'Email and Facebook integration',
      ],
      featured: false,
    },
    {
      id: 1,
      label: 'Growth',
      name: 'growth',
      price: 239,
      description: [
        'Best for growing businesses looking for more insights',
        'Above $1m USD annual revenue',
        'Above 50k customers',
      ],
      features: [
        '2 years of data analysis',
        '25,000 customers per sync',
        '5 team account',
        'Email and Facebook integration',
        'Advanced product analytics',
      ],
      featured: true,
    },
    {
      id: 0,
      label: 'Unlimited',
      name: 'unlimited',
      price: 849,
      description: [
        'Best for high volume merchants and large businesses',
        'Above $5m USD annual revenue',
        'Above 100k customers',
      ],
      features: [
        'Unlimited data analysis',
        'Unlimited customers per sync',
        '20 team account',
        'Email and Facebook integration',
        'Advanced product analytics',
        'Priority support',
      ],
      featured: false,
    },
  ];

  return (
    <div className="relative max-w-screen-xl px-5 mx-auto md:pl-segments-72px md:pr-20 py-11">
      <p className="font-bold leading-6">Change plan</p>
      <h1 className="mt-4 font-bold leading-10 text-segments-44px">
        Powerful segmentation
      </h1>
      <p className="mt-6 text-base">
        Upgrade to get immediate access to your segments and insights.
      </p>

      <div className="mt-6 text-segments-13px p-3 bg-[#FFECD6] rounded-xl inline-block">
        <div className="space-y-3 ">
          <div className="flex space-x-3">
            <ExclamationCircleIcon className="w-5 h-5" />
            <p className="text-black text-opacity-60">
              If you are in the 14-day trial, switching the plan will incur
              immediate billing. Please contact us for more information.
            </p>
          </div>
        </div>
      </div>

      <PriceTable plans={plans} />
    </div>
  );
}
